export type DmpChatTriggerAriaAttribute = 'aria-controls' | 'aria-expanded' | 'aria-haspopup' | 'aria-activedescendant'

export type DmpChatTriggerAriaProps = Partial<Record<DmpChatTriggerAriaAttribute, boolean | string>>

const dmpChatTriggerAriaAttributes: readonly DmpChatTriggerAriaAttribute[] = [
  'aria-controls',
  'aria-expanded',
  'aria-haspopup',
  'aria-activedescendant',
]

export interface DmpChatLexicalInputAccessibilityOptions {
  accessibleName: string
  describedBy?: string
  disabled: boolean
  placeholder?: string
  triggerAriaProps: DmpChatTriggerAriaProps
}

export function syncDmpChatLexicalInputAccessibility(
  contentEditable: HTMLElement,
  options: DmpChatLexicalInputAccessibilityOptions
): void {
  const { accessibleName, describedBy, disabled, placeholder, triggerAriaProps } = options

  contentEditable.setAttribute('aria-label', accessibleName)
  contentEditable.setAttribute('aria-disabled', String(disabled))
  contentEditable.setAttribute('aria-multiline', 'true')
  contentEditable.setAttribute('role', 'textbox')
  if (describedBy) {
    contentEditable.setAttribute('aria-describedby', describedBy)
  } else {
    contentEditable.removeAttribute('aria-describedby')
  }
  if (placeholder) {
    contentEditable.setAttribute('aria-placeholder', placeholder)
  } else {
    contentEditable.removeAttribute('aria-placeholder')
  }

  for (const attribute of dmpChatTriggerAriaAttributes) {
    const value = triggerAriaProps[attribute]
    if (value === undefined) {
      contentEditable.removeAttribute(attribute)
    } else {
      contentEditable.setAttribute(attribute, String(value))
    }
  }
}

export function shouldBlockDmpChatLexicalSubmit(
  event: Pick<KeyboardEvent, 'isComposing' | 'shiftKey'> | null,
  disabled: boolean
): boolean {
  return disabled && event !== null && !event.isComposing && !event.shiftKey
}

export interface DmpChatClipboardFileItem {
  readonly kind?: string
  getAsFile(): File | null
}

export interface DmpChatClipboardData {
  readonly files?: ArrayLike<File> | null
  readonly items?: ArrayLike<DmpChatClipboardFileItem> | null
}

export interface DmpChatClipboardPasteEvent {
  readonly clipboardData?: DmpChatClipboardData | null
  preventDefault(): void
}

const CLIPBOARD_IMAGE_EXTENSIONS: Readonly<Record<string, string>> = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

export function getClipboardDataFromPasteEvent(event: unknown): DmpChatClipboardData | null {
  if (typeof event !== 'object' || event === null || !('clipboardData' in event)) {
    return null
  }

  const clipboardData = (event as DmpChatClipboardPasteEvent).clipboardData
  return clipboardData ?? null
}

export function collectClipboardFiles(clipboardData: DmpChatClipboardData | null | undefined): File[] {
  if (!clipboardData) {
    return []
  }

  const fromFiles = Array.from(clipboardData.files ?? [])
  if (fromFiles.length > 0) {
    return fromFiles
  }

  const fromItems: File[] = []
  for (const item of Array.from(clipboardData.items ?? [])) {
    if (item.kind !== 'file') {
      continue
    }

    const file = item.getAsFile()
    if (file) {
      fromItems.push(file)
    }
  }

  return fromItems
}

export function nameClipboardFiles(files: readonly File[]): File[] {
  const unnamedCount = files.filter((file) => !hasUsableClipboardFileName(file.name)).length
  let unnamedIndex = 0
  return files.map((file) => {
    if (hasUsableClipboardFileName(file.name)) {
      return file
    }

    unnamedIndex += 1
    const suffix = unnamedCount > 1 ? `-${unnamedIndex}` : ''
    return new File([file], `screenshot${suffix}.${extensionForClipboardFile(file)}`, {
      lastModified: file.lastModified,
      type: file.type || 'image/png',
    })
  })
}

export function shouldAttachClipboardFilesOnPaste({
  attachmentsSupported,
  disabled,
  files,
}: {
  attachmentsSupported: boolean
  disabled: boolean
  files: readonly File[]
}): boolean {
  return !disabled && attachmentsSupported && files.length > 0
}

export async function attachClipboardFiles(
  files: readonly File[],
  addAttachment: (file: File) => Promise<void>
): Promise<void> {
  await Promise.all(
    files.map(async (file) => {
      try {
        await addAttachment(file)
      } catch {
        // The composer runtime emits composer.attachmentAddError before rejecting.
      }
    })
  )
}

function hasUsableClipboardFileName(name: string): boolean {
  const trimmed = name.trim()
  return trimmed.length > 0 && !trimmed.includes('/') && !trimmed.includes('\\') && !trimmed.includes('..')
}

function extensionForClipboardFile(file: File): string {
  const mediaType = file.type.toLowerCase()
  const mapped = CLIPBOARD_IMAGE_EXTENSIONS[mediaType]
  if (mapped) {
    return mapped
  }

  if (mediaType.startsWith('image/')) {
    const subtype = mediaType.slice('image/'.length)
    if (/^[a-z0-9]+$/.test(subtype)) {
      return subtype
    }
  }

  return 'png'
}
